import React, { FC, useEffect, useMemo } from "react";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
} from "../../components";
import { useSiteCore } from "../../hooks/useSiteCore";
import { ViewTopicWrapper } from "./MdmViewTopic.style";
import {
  EMPTY_VALUE_DASH,
  LANGUAGE_CODE,
  PAGE_IDS,
} from "../../helpers/constants";
import { getValueOrBlank, replaceHashWithValue } from "../../helpers/utils";
import { localeFields } from "../../helpers/constants/siteCoreKeys";
import { userPreferredLanguageSelector } from "../../redux/slices/appState/appStateSelector";
import { useDispatch, useSelector } from "react-redux";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { addTopicSiteCoreKeys } from "../mdmAddTopic/siteCore/SiteCoreKeys";
import { TopicDetails } from "./components/topicDetails/TopicDetails";
import ExceptionDetails from "./components/exceptionDetails/ExceptionDetails";
import { AddExceptionsType, LocaleType } from "./modelsAndSchemas/models";
import { useGetMdmTopicDetailsById } from "../../api/services/mdmActivity/mdmActivityApis";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { useGridContainer } from "../../hooks/useGridContainer";
import { selectedUnitSelector } from "../../redux/slices/mdmActivity/mdmActivitySelector";

export const pageId = PAGE_IDS.MDM_VIEW_TOPIC;

interface MdmViewTopicPropsType {
  show: boolean;
  handleCloseModal: () => void;
  editButtonClick?: () => void;
  topicId: string;
}

export const MdmViewTopic: FC<MdmViewTopicPropsType> = ({
  show,
  handleCloseModal,
  editButtonClick,
  topicId,
}) => {
  const { commonLabels, fetchLabel } = useSiteCore();
  const storedUnit = useSelector(selectedUnitSelector);

  const { fetchLanguageDropdownValues } = useNavigationSiteCore();

  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const {
    data: topicData,
    isFetching,
    isError: isTopicDataError,
  } = useGetMdmTopicDetailsById(topicId);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isFetching,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isFetching]);

  useEffect(() => {
    if (isTopicDataError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isTopicDataError]);

  const {
    addTopic,
    topicDetailsHeader,
    localeTopicHeader,
    addExceptionsHeader,
    viewTopicHeader,
    editTopicLink,
  } = addTopicSiteCoreKeys;

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const getViewTopicLabel = (key: string) => {
    return fetchLabel(addTopic, key);
  };

  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );

  // set key value pare data and sort mandatory to first
  const mergedLocaleLanguages = useMemo(() => {
    const siteCoreLanguages = fetchLanguageDropdownValues();
    const localeKeys = localeFields(commonLabels);
    return siteCoreLanguages
      .map((language) => {
        const placeholderMatching = localeKeys.find(
          (placeholder) => placeholder.key === language.value,
        );
        return {
          ...language,
          ...placeholderMatching,
          isMandatory: userSelectedLanguage === language.value,
          isEnglish:
            language.value === LANGUAGE_CODE.EN_US &&
            userSelectedLanguage !== LANGUAGE_CODE.EN_GB,
        };
      })
      .sort((a, b) => (b.isEnglish ? 1 : 0) - (a.isEnglish ? 1 : 0))
      .sort((a, b) => (b.isMandatory ? 1 : 0) - (a.isMandatory ? 1 : 0));
  }, [userSelectedLanguage, localeFields, fetchLanguageDropdownValues]);

  const getTopicLocaleValues = (localeKey: string) => {
    const template = topicData?.topicLocales.find(
      (data: LocaleType) => data.localeKey === localeKey,
    );

    if (template) {
      return template.localeName;
    }

    return EMPTY_VALUE_DASH;
  };

  return (
    <MuiModal
      open={show}
      onClose={handleCloseModal}
      title={replaceHashWithValue(
        getViewTopicLabel(viewTopicHeader),
        getValueOrBlank(topicData?.topicName),
      )}
      secondaryButtonTitle={commonLabels.back}
      onSecondaryButtonClick={handleCloseModal}
      tertiaryButtonTitle={getViewTopicLabel(editTopicLink)}
      onTertiaryButtonClick={editButtonClick}
      hasLargeContent
      className="mdm-view-top-details-modal"
    >
      <ViewTopicWrapper data-testid={`${pageId}-view-topic`}>
        <MuiBox className="accordion-header" data-testid="topic-details">
          <AccordionHeader
            title={renderHeader(getViewTopicLabel(topicDetailsHeader))}
            isExpanded={true}
          >
            <TopicDetails
              pageId={pageId}
              topicDetailsData={topicData}
              selectedUnit={storedUnit}
            />
          </AccordionHeader>
        </MuiBox>

        <MuiBox className="accordion-header" data-testid="locale-topic">
          <AccordionHeader
            title={renderHeader(getViewTopicLabel(localeTopicHeader))}
            isExpanded={true}
          >
            <MuiBox className="locale-container">
              <MuiGrid container data-testid="view-locale">
                {mergedLocaleLanguages.map(
                  (
                    { key, label }: any, //any with type
                  ) => (
                    <MuiGrid
                      key={key}
                      size={gridSize}
                      className={gridSizeMediumClass}
                    >
                      <MuiDoubleLabel
                        id={`${pageId}-${key}-label`}
                        label={label}
                        value={getTopicLocaleValues(key)}
                      />
                    </MuiGrid>
                  ),
                )}
              </MuiGrid>
            </MuiBox>
          </AccordionHeader>
        </MuiBox>

        <MuiBox className="accordion-header" data-testid="exceptions">
          <AccordionHeader
            title={renderHeader(getViewTopicLabel(addExceptionsHeader))}
            isExpanded={true}
          >
            <ExceptionDetails
              pageId={pageId}
              selectedUnit={storedUnit}
              exceptionData={
                (topicData?.addExceptions as AddExceptionsType[]) ?? []
              }
            />
          </AccordionHeader>
        </MuiBox>
      </ViewTopicWrapper>
    </MuiModal>
  );
};
